import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { ArrowLeftIcon, PencilIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import JournalForm from '../components/forms/JournalForm';
import { JournalEntry } from '../types/journal';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Badge, Divider } from '../design/primitives';
import { Button, Spinner } from '../components/ui';

const emotionsMap: { [key: string]: string } = {
  happy: '😊',
  sad: '😢',
  angry: '😠',
  surprised: '😮',
  calm: '😌',
  anxious: '😟',
  excited: '🤩',
  tired: '😴',
  confused: '😕',
};

const entryTypeColors: { [key: string]: string } = {
  reference: COLORS.status.info,
  thought: COLORS.accent.default,
  knowledge: COLORS.status.success,
  investigation: COLORS.status.warning,
  experiment: '#9333ea',
  discovery: '#06b6d4',
  decision: '#f97316',
};

const JournalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchEntry = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/api/journal_entries/${id}/`);
      setEntry(response.data);
    } catch (err) {
      console.error('Error fetching journal entry:', err);
      setError('Failed to load journal entry.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchEntry();
    }
  }, [id, fetchEntry]);

  const handleEntryUpdated = (updatedEntry: JournalEntry) => {
    setEntry(updatedEntry);
    setIsEditOpen(false);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        padding: SPACING[24]
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <Panel style={{ textAlign: 'center', padding: SPACING[48] }}>
            <ExclamationTriangleIcon style={{
              width: '48px',
              height: '48px',
              color: COLORS.status.error,
              margin: '0 auto',
              marginBottom: SPACING[16]
            }} />
            <h2 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
              marginBottom: SPACING[8]
            }}>
              Error
            </h2>
            <p style={{
              color: COLORS.text.secondary,
              fontSize: TYPOGRAPHY.size.sm,
              margin: 0,
              marginBottom: SPACING[16]
            }}>
              {error || 'Journal entry not found.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/journal')}>
              Back to Journal
            </Button>
          </Panel>
        </div>
      </div>
    );
  }

  const entryTypeLabel = entry.entry_type_display || entry.entry_type || 'Thought';
  const entryTypeColor = entryTypeColors[entry.entry_type || 'thought'] || COLORS.accent.default;

  return (
    <div style={{
      backgroundColor: COLORS.bg.base,
      minHeight: '100vh',
      padding: SPACING[24]
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Back button */}
        <button
          onClick={() => navigate('/journal')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: SPACING[4],
            color: COLORS.text.secondary,
            fontSize: TYPOGRAPHY.size.sm,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            marginBottom: SPACING[16]
          }}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back to Journal
        </button>

        {/* Header */}
        <Panel style={{ marginBottom: SPACING[16] }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: SPACING[16]
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
              {entry.emotion && emotionsMap[entry.emotion] && (
                <span style={{ fontSize: '32px' }} title={entry.emotion}>
                  {emotionsMap[entry.emotion]}
                </span>
              )}
              <div>
                <h1 style={{
                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                  fontSize: TYPOGRAPHY.headings.sizes.h2,
                  fontWeight: TYPOGRAPHY.headings.weight,
                  color: COLORS.text.primary,
                  margin: 0
                }}>
                  {formatDate(entry.date) || 'Journal Entry'}
                </h1>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[8],
                  marginTop: SPACING[4]
                }}>
                  <Badge style={{
                    backgroundColor: `${entryTypeColor}20`,
                    color: entryTypeColor,
                    fontSize: TYPOGRAPHY.size.xs
                  }}>
                    {entryTypeLabel}
                  </Badge>
                  {entry.status && entry.status !== 'active' && (
                    <Badge style={{
                      backgroundColor: entry.status === 'superseded'
                        ? `${COLORS.status.warning}20`
                        : `${COLORS.text.tertiary}20`,
                      color: entry.status === 'superseded'
                        ? COLORS.status.warning
                        : COLORS.text.tertiary,
                      fontSize: TYPOGRAPHY.size.xs
                    }}>
                      {entry.status}
                    </Badge>
                  )}
                  {entry.affects_production && (
                    <Badge style={{
                      backgroundColor: `${COLORS.status.error}20`,
                      color: COLORS.status.error,
                      fontSize: TYPOGRAPHY.size.xs
                    }}>
                      Affects Production
                    </Badge>
                  )}
                  {entry.emotion && (
                    <span style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.text.tertiary,
                      textTransform: 'capitalize'
                    }}>
                      {entry.emotion}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditOpen(true)}
              leftIcon={<PencilIcon style={{ width: '14px', height: '14px' }} />}
            >
              Edit
            </Button>
          </div>

          <Divider />

          {/* Content */}
          <div style={{ marginTop: SPACING[16] }}>
            <p style={{
              fontSize: TYPOGRAPHY.size.base,
              color: COLORS.text.primary,
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              margin: 0
            }}>
              {entry.content}
            </p>
          </div>
        </Panel>

        {/* Metadata */}
        {(entry.categories?.length || entry.project || entry.business || entry.related_people?.length || entry.tags) && (
          <Panel>
            <h3 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.semibold,
              color: COLORS.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              margin: 0,
              marginBottom: SPACING[12]
            }}>
              Details
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: `${SPACING[8]}px ${SPACING[16]}px`,
              fontSize: TYPOGRAPHY.size.sm
            }}>
              {entry.categories && entry.categories.length > 0 && (
                <>
                  <dt style={{ color: COLORS.text.tertiary, fontWeight: TYPOGRAPHY.weight.medium }}>Categories</dt>
                  <dd style={{ margin: 0, display: 'flex', gap: SPACING[4], flexWrap: 'wrap' }}>
                    {entry.categories.map(cat => (
                      <Badge key={cat.id} style={{
                        backgroundColor: cat.color ? `${cat.color}20` : `${COLORS.accent.default}20`,
                        color: cat.color || COLORS.accent.default,
                        fontSize: TYPOGRAPHY.size.xs
                      }}>
                        {cat.name}
                      </Badge>
                    ))}
                  </dd>
                </>
              )}

              {entry.project && (
                <>
                  <dt style={{ color: COLORS.text.tertiary, fontWeight: TYPOGRAPHY.weight.medium }}>Project</dt>
                  <dd style={{ margin: 0 }}>
                    <button
                      onClick={() => navigate(`/projects/${entry.project!.id}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.accent.default,
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: TYPOGRAPHY.size.sm
                      }}
                    >
                      {entry.project.name}
                    </button>
                  </dd>
                </>
              )}

              {entry.business && (
                <>
                  <dt style={{ color: COLORS.text.tertiary, fontWeight: TYPOGRAPHY.weight.medium }}>Business</dt>
                  <dd style={{ margin: 0 }}>
                    <button
                      onClick={() => navigate(`/businesses/${entry.business!.id}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: COLORS.accent.default,
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: TYPOGRAPHY.size.sm
                      }}
                    >
                      {entry.business.name}
                    </button>
                  </dd>
                </>
              )}

              {entry.related_people && entry.related_people.length > 0 && (
                <>
                  <dt style={{ color: COLORS.text.tertiary, fontWeight: TYPOGRAPHY.weight.medium }}>People</dt>
                  <dd style={{ margin: 0, display: 'flex', gap: SPACING[4], flexWrap: 'wrap' }}>
                    {entry.related_people.map(person => (
                      <button
                        key={person.id}
                        onClick={() => navigate(`/people/${person.id}`)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: COLORS.accent.default,
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: TYPOGRAPHY.size.sm
                        }}
                      >
                        {person.name}
                      </button>
                    ))}
                  </dd>
                </>
              )}

              {entry.tags && (
                <>
                  <dt style={{ color: COLORS.text.tertiary, fontWeight: TYPOGRAPHY.weight.medium }}>Tags</dt>
                  <dd style={{ margin: 0, display: 'flex', gap: SPACING[4], flexWrap: 'wrap' }}>
                    {entry.tags.split(',').map((tag, idx) => (
                      <Badge key={idx} style={{
                        backgroundColor: `${COLORS.text.tertiary}15`,
                        color: COLORS.text.secondary,
                        fontSize: TYPOGRAPHY.size.xs
                      }}>
                        {tag.trim()}
                      </Badge>
                    ))}
                  </dd>
                </>
              )}
            </div>
          </Panel>
        )}
      </div>

      {/* Edit Modal */}
      <JournalForm
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onJournalEntryCreated={() => {}}
        initialEntry={entry}
        isEditMode={true}
        onJournalEntryUpdated={handleEntryUpdated}
      />
    </div>
  );
};

export default JournalDetailPage;
